'use client'
import { useRef, useEffect } from 'react'

import {
  useDeviceInfo,
  sendGTMEvent,
  useBraze,
  type InAppMessage,
} from '@b-stock/fe-core/bstock-next/components'
import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'

import { usePageFeatureBadges } from '@/helpers/featureBadges/pageFeatureBadges'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import useBrazeIdentityResolved from '@/helpers/telemetry/useBrazeIdentityResolved'

export default function TrackPageView({
  pageName,
  additionalData,
}: {
  pageName: string
  additionalData?: Record<string, string | number | boolean | null | undefined>
}) {
  const analytics = useAnalytics()
  const braze = useBraze()
  const identityResolved = useBrazeIdentityResolved()
  const { deviceType } = useDeviceInfo()
  const pageFeatureBadges = usePageFeatureBadges()

  // Track if Segment page view has been sent (only once per page)
  const hasTrackedSegment = useRef(false)
  const hasTrackedGtm = useRef(false)

  // Track the currently displayed in-app message for programmatic closing
  const currentInAppMessage = useRef<InAppMessage | null>(null)

  // Track Segment page view - runs immediately when analytics is available
  useEffect(() => {
    if (!hasTrackedSegment.current) {
      // Left off when no badge is on screen; a missing property reads as NULL
      const featureBadges = pageFeatureBadges()
      analytics.pageViewed({
        screenName: pageName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        url: document.URL,
        referrer: document.referrer,
        ...(featureBadges && { feature_badges: featureBadges }),
        ...additionalData,
      })
      hasTrackedSegment.current = true
    }
  }, [analytics, pageName, additionalData, pageFeatureBadges])

  useEffect(() => {
    if (hasTrackedGtm.current) return

    sendGTMEvent({
      event: 'page_view',
      platform: GTM_PLATFORM,
      page_type: additionalData?.page_type ?? pageName,
      page_variation: additionalData?.page_variation,
      page_name: additionalData?.page_name ?? pageName,
    })

    hasTrackedGtm.current = true
  }, [additionalData, pageName])

  // Subscribe to Braze in-app messages to track the current message
  useEffect(() => {
    if (braze && braze.subscribeToInAppMessage) {
      braze.subscribeToInAppMessage((message) => {
        // Store reference to the displayed message
        currentInAppMessage.current = message
      })
    }
  }, [braze])

  // Close any open Braze campaigns when page changes and request new ones
  useEffect(() => {
    // Programmatically close the current message if one is displayed
    if (currentInAppMessage.current) {
      currentInAppMessage.current.closeMessage()
      currentInAppMessage.current = null
    }

    // Forcefully remove any remaining Braze in-app message elements
    // Braze checks for .ab-modal-interactions to determine if a message is showing
    if (typeof document !== 'undefined') {
      // Remove all modal interactions (the check Braze uses)
      const modalInteractions = document.querySelectorAll(
        '.ab-modal-interactions'
      )
      modalInteractions.forEach((el) => el.remove())

      // Remove the root container
      const brazeIAMRoot = document.querySelector('.ab-iam-root')
      if (brazeIAMRoot) {
        brazeIAMRoot.remove()
      }

      // Remove pause-scrolling classes
      document.body.classList.remove('ab-pause-scrolling')
      document.documentElement.classList.remove('ab-pause-scrolling')
    }

    // After cleanup, track the new page view and request campaigns
    if (!braze || !identityResolved) {
      return
    }

    const timer = setTimeout(() => {
      // Track the new page view which triggers campaigns
      trackBrazePageView(braze, pageName, deviceType, additionalData)

      // Flush events to server
      if (braze.requestImmediateDataFlush) {
        braze.requestImmediateDataFlush()
      }
      // Request new in-app messages from server
      if (braze.requestInAppMessageRefresh) {
        braze.requestInAppMessageRefresh()
      }
    }, 200)

    // Without this, navigating again inside the 200ms window fires the stale
    // callback and attributes a page view to the page the user already left.
    return () => clearTimeout(timer)
  }, [pageName, braze, identityResolved, deviceType, additionalData])

  return null
}

function trackBrazePageView(
  braze: ReturnType<typeof useBraze>,
  pageName: string,
  deviceType: string,
  additionalData?: Record<string, string | number | boolean | null | undefined>
) {
  // Only track on Home Page, All Auctions, First Dibs and Seller Storefront pages for now
  const trackablePages: string[] = [
    ScreenName.AllAuction,
    ScreenName.FirstDibs,
    ScreenName.Homepage,
    ScreenName.SellerStorefront,
  ]
  if (!trackablePages.includes(pageName)) return

  // If Braze is not available, skip tracking
  if (!braze) {
    return
  }

  const eventProperties: Record<string, string | number | boolean> = {
    page_name: pageName,
    url: document.URL,
    device_type: deviceType,
    ...additionalData,
  }

  braze.logCustomEvent('opened_page', eventProperties)
}
